import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFonts, themeFontsSize, themeFontWeights } from "@GeriMed/styles/variables";
import * as React from "react";
import { css } from "styled-components";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useState, useCallback } from "react";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import ArrowCircle from "@GeriMed/Icons/ArrowCircle";
import Link from "@insite/mobius/Link";
import Clickable from "@insite/mobius/Clickable";

const enum fields {
    title = "title",
    brandData = "brandData",
    autoplay = "autoplay",
}

interface BrandItem {
    fields: {
        brandTitle: string;
        brandUrl: string; 
        brandImage: string;
    }
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandData]: BrandItem[];
        [fields.autoplay]?: number;
    };
}

export interface BrandSliderStyles {
    wrapper?: InjectableCss;
    headingWrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
    carouselViewport?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    navButton?: InjectableCss;
    paginationDots?: InjectableCss;
}

export const BrandSliderStyles: BrandSliderStyles = {
    wrapper: {
        css: css`
            padding-bottom: ${themeFontsSize.size20};
            text-align: center;
            .carousel-view{
                overflow: hidden;
                max-width: 1200px;
                position: relative;
                width: 100%;
                margin: 0 auto;
                overflow: hidden;
            }   
            .carousel-view > div {
                display: flex;
                gap: 10px;
            }
            .slides-view{
                display: flex;
            }
            .slides-view > * {
                flex: 0 0 calc(100% / 8); /* Shows 3 slides at a time */
            }
            .slider-container{
                display: flex;
                max-width: 1200px;
                margin: 0 auto;
                width: 100%;
                img{
                    width: 184px;
                }
            }
            .arrow-button{
                left: 0;
                font-size: ${themeFontsSize.size20};
                top: 30px;
                position: relative;
                background: none;
                border: none;
                height: ${themeFontsSize.size28};
                &.leftArrow{
                    transform: translateY(-50%);
                    cursor: pointer;
                    transform: rotate(180deg);
                }
            }
            .pagination-dot{
                display: flex;
                justify-content: center;
                margin-top: ${themeFontsSize.size10};
                margin: ${themeFontsSize.size20};
            }
            .products-wrap [data-test-selector="GridOffset--"]{
                max-width: 1286px;
            }
            .arrow-button svg {
                width: 20px;
              }
        `,
    },
};

const styles = BrandSliderStyles;

const BrandSliderComponent: React.FC<OwnProps> = ({ fields }) => {

    const [emblaRef, emblaApi] = useEmblaCarousel({align: "start", axis: "x", containScroll: "trimSnaps", loop: false});
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const { brandData, autoplay = 5 } = fields; 

    if(!brandData || brandData.length === 0)
    {
        return null;
    }

    useEffect(() => {
        if (!emblaApi) return;
    
        let autoplayInterval: number | null = null;
        setScrollSnaps(emblaApi.scrollSnapList());
        const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
        emblaApi.on('select', onSelect);
        onSelect(); // Initialize selectedIndex correctly on mount
    
        const scrollToNextOrFirst = () => {
            const currentIndex = emblaApi.selectedScrollSnap();
            const totalSlides = emblaApi.scrollSnapList().length;
    
            if (currentIndex === totalSlides - 1) {
                emblaApi.scrollTo(0); // Go back to first slide
            } else {
                emblaApi.scrollNext(); // Go to next slide
            }
        };
    
        autoplayInterval = window.setInterval(scrollToNextOrFirst, autoplay * 800);
        return () => {
            if (autoplayInterval) window.clearInterval(autoplayInterval);
            emblaApi.off('select', onSelect); // Clean event listener
        };
    }, [emblaApi, autoplay]);
    

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper className="slider-container">
                <Clickable {...styles.navButton} className="arrow-button leftArrow" onClick={scrollPrev}>
                    <ArrowCircle/>
                </Clickable>
                <StyledWrapper className="carousel-view" ref={emblaRef}>
                    <StyledWrapper {...styles.carouselContainer} className="slides-view">
                        {brandData.map((brand, index) => (
                            <Link href={brand.fields.brandUrl}>
                            <StyledWrapper key={index} {...styles.carouselSlide}>
                                <img src={brand.fields.brandImage} alt={brand.fields.brandTitle} />
                            </StyledWrapper>
                            </Link>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                <Clickable {...styles.navButton} className="arrow-button rightArrow" onClick={scrollNext}>
                    <ArrowCircle/>
                </Clickable>
            </StyledWrapper>
            <StyledWrapper className="pagination-dot">
            {scrollSnaps.map((_, index) => (
                <StyledWrapper
                    key={index}
                    style={{
                        width: "10px",
                        height: "10px",
                        margin: "5px",
                        borderRadius: "50%",
                        background: index === selectedIndex ? "#1893f8" : "#ccc",
                        border: "none",
                        cursor: "pointer",
                    }}
                    onClick={() => emblaApi && emblaApi.scrollTo(index)}
                />
            ))}
        </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BrandSliderComponent,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.brandData,
                editorTemplate: "ListField",
                displayName: "Brand Details",
                getDisplay: (item: HasFields) => {
                    const {
                        brandTitle,
                    } = item.fields;
                    if (brandTitle) {
                        return brandTitle;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 2,
                fieldDefinitions: [
                    {
                        name: "brandTitle",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "brandUrl",
           				editorTemplate: "TextField",
                        displayName: "URL",
						defaultValue: "",
                        sortOrder: 1,
                        isRequired: true,
                    },
                    {
                        name: "brandImage",
                        displayName: 'Image',
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        sortOrder: 2,
                        isRequired: true,
                    },
                    {
                        name: fields.autoplay,
                        editorTemplate: "IntegerField",
                        displayName: "Autoplay (seconds)",
                        defaultValue: 3,
                        sortOrder: 3,
                        isRequired: false,
                    }
                ],
            },
        ],
    },
};

export default widgetModule;


